// Modules
import {
  Action,
  createSlice,
  ThunkAction,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";

// Types
import { EventProps, EventState } from "./../../types/events";
import { Facet } from "../../types/facets";

// Reducers
import { ADD_FACETS, EMPTY_FACETS, APPEND_FACET } from "./facets.reducer";

// Constants
import Constant, { constant } from "../../constants/contant";

// Utils
import { getAllEventDates } from "../../utils/facets.utils";

// State Initialization
const initialState: EventState = {
  list: [],
  loading: false,
  requestUrl: `${Constant.API_BASE_URL}en/${Constant.EVENTS_BASE_PATH}${Constant.INCLUDES}`,
  highlight: {} as EventProps,
  highlightId: null,
  highlightIncluded: [],
  dates: [],
  facetsLoaded: false,
  included: [],
  nextPage: "",
  selectedEvent: {} as EventProps,
  selectedEventIncluded: [],
  eventStatus: true,
  eventDomainField: null,
};

// Reducer Slices
const slice = createSlice({
  name: "event",
  initialState,
  reducers: {
    EVENTS_REQUESTED: (event): void => {
      event.loading = true;
    },
    SET_EVENTS: (event, action: any): void => {
      event.list = action.payload;
      event.dates = [];
    },
    APPEND_EVENTS: (event, action: any): void => {
      event.list = [...event.list, ...action.payload];
    },
    APPEND_EVENT_DATES: (event, action: any): void => {
      event.dates = [...event.dates, ...action.payload];
    },
    APPEND_INCLUDED: (event, action: any): void => {
      event.included = [...event.included, ...action.payload];
    },
    SET_INCLUDED: (event, action: any): void => {
      event.included = action.payload;
    },
    SET_HIGHLIGHT_INCLUDED: (event, action: any): void => {
      event.highlightIncluded = action.payload;
    },
    SET_SELECTED_EVENT: (event, action: any): void => {
      event.selectedEvent = action.payload;
    },
    SET_SELECTED_EVENT_INCLUDED: (event, action: any): void => {
      event.selectedEventIncluded = action.payload;
    },
    SET_HIGHLIGHT_EVENTS: (event, action: any): void => {
      event.highlight = action.payload;
    },
    SET_HIGHLIGHT_EVENTS_ID: (event, action: any): void => {
      event.highlightId = action.payload;
    },
    SET_EVENT_DATES: (event, action: any): void => {
      if (event.list.length) {
        event.dates = [...event.dates, ...action.payload];
      } else {
        event.dates = [...action.payload];
      }
    },
    SET_NEXT_PAGE: (event, action: any): void => {
      event.nextPage = action.payload;
    },
    UPDATE_REQUEST_URL: (event, action: any): void => {
      event.requestUrl = action.payload;
    },
    EVENTS_REQUEST_SUCCESS: (event): void => {
      event.loading = false;
    },
    FACETS_LOADED: (event): void => {
      event.facetsLoaded = true;
    },
    UPDATE_EVENT_STATUS: (event, action: any): void => {
      event.eventStatus = action.payload;
    },
    UPDATE_EVENT_DOMAIN: (event, action: any): void => {
      event.eventDomainField = action.payload;
    },
  },
});

// Actions
const {
  EVENTS_REQUESTED,
  SET_EVENTS,
  EVENTS_REQUEST_SUCCESS,
  UPDATE_REQUEST_URL,
  SET_HIGHLIGHT_EVENTS_ID,
  SET_HIGHLIGHT_EVENTS,
  SET_EVENT_DATES,
  FACETS_LOADED,
  SET_INCLUDED,
  SET_NEXT_PAGE,
  APPEND_EVENTS,
  APPEND_EVENT_DATES,
  APPEND_INCLUDED,
  SET_HIGHLIGHT_INCLUDED,
  SET_SELECTED_EVENT,
  SET_SELECTED_EVENT_INCLUDED,
  UPDATE_EVENT_STATUS,
  UPDATE_EVENT_DOMAIN,
} = slice.actions;

// Reducer
export default slice.reducer;

// Get All Available and Set Facets
export const getEvents =
  (url: string): ThunkAction<void, EventState, void, Action> =>
  async (
    dispatch: ThunkDispatch<EventState, void, Action>,
    store: any
  ): Promise<void> => {
    try {
      const { facet, common } = store();
      await dispatch({ type: EVENTS_REQUESTED.type });

      if (!url.includes(common.lang)) {
        const termInUrl = common.lang === "he" ? "en/jsonapi" : "/jsonapi";
        const termToChange = common.lang === "he" ? "jsonapi" : "en/jsonapi";
        url = url.replace(termInUrl, termToChange);
      }
      const { data }: AxiosResponse = await axios.get(
        `${url}&filter[langcode]=${common.lang}`
      );
      await dispatch({ type: SET_EVENTS.type, payload: data.data });
      await dispatch({ type: SET_INCLUDED.type, payload: data.included });
      const eventDates: Array<Date> = getAllEventDates(data.data);
      await dispatch({ type: SET_EVENT_DATES.type, payload: eventDates });
      await dispatch({
        type: SET_NEXT_PAGE.type,
        payload: data.links?.next ? data.links.next.href : "",
      });
      await dispatch({ type: EMPTY_FACETS.type });

      // if (!Object.keys(facet).length) {
      await Promise.all(
        data.meta.facets.map(async (item: any) => {
          if (item["id"] === "event_type") {
            const facet = new Facet(item);

            await Promise.all(
              facet.values.map(async (value: any) => {
                  await dispatch({
                    type: APPEND_FACET.type,
                    payload: value,
                  });
              })
            );
          }

          if (item["id"] === "event_interest_domains") {
            const facet = new Facet(item);
            await dispatch({ type: ADD_FACETS.type, payload: facet.values });
          }
        })
      );
      await dispatch({ type: FACETS_LOADED.type });
      // }
      await dispatch({ type: EVENTS_REQUEST_SUCCESS.type });
    } catch (err) {
      await dispatch({ type: EVENTS_REQUEST_SUCCESS.type });
    }
  };

// Update Request Url With Or Without Filters
export const updateRequestUrl =
  (url: string): ThunkAction<void, EventState, void, Action> =>
  (dispatch: ThunkDispatch<EventState, void, Action>): void => {
    dispatch({ type: UPDATE_REQUEST_URL.type, payload: url });
  };

// Get Highligh Event
export const getHighlightEvent =
  (): ThunkAction<void, EventState, void, Action> =>
  async (
    dispatch: ThunkDispatch<EventState, void, Action>,
    store: any
  ): Promise<void> => {
    const { common } = store();
    let url: string = `${Constant.URL}${
      common.lang === "en" ? "/en" : ""
    }/api/get_highlight_event`;
    const { data: eventId }: AxiosResponse = await axios.get(url);
    url = `${Constant.API_BASE_URL}${common.lang === "en" ? "en/" : ""}${
      Constant.EVENTS_BASE_PATH
    }${Constant.INCLUDES}&filter[nid]=${eventId}`;

    const { data: highlightEvent }: AxiosResponse = await axios.get(url);
    
    await dispatch({
      type: SET_HIGHLIGHT_EVENTS.type,
      payload: highlightEvent.data[0],
    });
    await dispatch({
      type: SET_HIGHLIGHT_INCLUDED.type,
      payload: highlightEvent.included,
    });
    await dispatch({
      type: SET_HIGHLIGHT_EVENTS_ID.type,
      payload: parseInt(eventId),
    });
  };

// Get Event
export const getEvent =
  (eventId: string): ThunkAction<void, EventState, void, Action> =>
  async (
    dispatch: ThunkDispatch<EventState, void, Action>,
    store: any
  ): Promise<void> => {
    const { common } = store();
    let url = `${Constant.API_BASE_URL}${common.lang == "en" ? "en/" : ""}${
      Constant.EVENTS_BASE_PATH
    }${Constant.INCLUDES}&filter[nid]=${eventId}`;

    const { data: event }: AxiosResponse = await axios.get(url);
    await dispatch({
      type: SET_SELECTED_EVENT.type,
      payload: event.data[0],
    });
    await dispatch({
      type: SET_SELECTED_EVENT_INCLUDED.type,
      payload: event.included,
    });
    await dispatch({ type: EMPTY_FACETS.type });

    await Promise.all(
      event.meta.facets.map(async (item: any) => {
        if (
          item["id"] === "organizing_unit" ||
          item["id"] === "event_type" ||
          item["id"] === "event_interest_domains"
        ) {
          const facet = new Facet(item);
          await dispatch({ type: ADD_FACETS.type, payload: facet.values });
        }
      })
    );
    await dispatch({ type: FACETS_LOADED.type });
  };

export const getNextSetOfEvents =
  (): ThunkAction<void, EventState, void, Action> =>
  async (
    dispatch: ThunkDispatch<EventState, void, Action>,
    store: any
  ): Promise<void> => {
    try {
      const { event }: any = store();
      await dispatch({ type: EVENTS_REQUESTED.type });
      const { data }: AxiosResponse = await axios.get(event.nextPage);
      await dispatch({ type: APPEND_EVENTS.type, payload: data.data });
      await dispatch({ type: APPEND_INCLUDED.type, payload: data.included });
      const eventDates: Array<Date> = getAllEventDates(data.data);
      await dispatch({ type: APPEND_EVENT_DATES.type, payload: eventDates });
      await dispatch({
        type: SET_NEXT_PAGE.type,
        payload: data.links?.next ? data.links.next.href : "",
      });
      await dispatch({ type: EVENTS_REQUEST_SUCCESS.type });
    } catch (err) {}
  };

// Update Event State
export const udpateEventStatus =
  (status: boolean): ThunkAction<void, EventState, void, Action> =>
  async (
    dispatch: ThunkDispatch<EventState, void, Action>,
    store: any
  ): Promise<void> => {

    await dispatch({
      type: UPDATE_EVENT_STATUS.type,
      payload: status
    });
  };

  // Get domain field 

export const getDomainField = 
(domainField: string): ThunkAction<void, EventState, void, Action> =>
async (
  dispatch: ThunkDispatch<EventState, void, Action>,
  store: any
): Promise<void> => {
  const { common } = store();
  let url: string = `${Constant.URL}${
    common.lang === "en" ? "/en" : ""
  }${Constant.EVENTS_CULC_UNITS_PATH}=${domainField}`;
  const { data }: AxiosResponse = await axios.get(url);
  await dispatch({
    type: UPDATE_EVENT_DOMAIN.type,
    payload: data.data[0].attributes?.drupal_internal__revision_id,
  });
};